import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { TOKEN_PATH, LOGOUT_PATH } from './data-access/auth.api';
import { AuthStore } from './auth.store';

/**
 * `/connect/token` issues no refresh token, so an expired session cannot be
 * renewed silently — a 401 ends it and hands the user to the 401 page, which
 * offers the way back in. This intentionally replaces the refresh interceptor
 * the specs anticipated.
 *
 * Two endpoints are excluded. A 401 from the token endpoint is a rejected
 * sign-in rather than an expired session, and is left for the caller to report;
 * a 401 from the revoke endpoint belongs to a sign-out that is already on its
 * way out, and starting an expiry exit there would race `logout()`'s own.
 */
export const apiErrorInterceptor: HttpInterceptorFn = (request, next) => {

  // `AuthStore` is resolved on demand rather than injected up front, because it
  // pulls in `AuthApi` and therefore `HttpClient` — the cycle `session-token.ts`
  // warns about. This interceptor sees every request the app makes, including
  // the translation bundles fetched before the app initializer runs, so
  // reaching for the store only when a session actually ends keeps that whole
  // graph off the happy path.
  const injector = inject(Injector);
  const endExpiredSession = () => void injector.get(AuthStore).kickOutFromSession();

  return next(request).pipe(
    catchError((error: unknown) => {
      const sessionRejected =
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !request.url.includes(TOKEN_PATH) &&
        !request.url.includes(LOGOUT_PATH);

      if (sessionRejected) {
        endExpiredSession();
      }

      return throwError(() => error);
    }),
  );
};
